import React, { useCallback } from 'react';
import moment from 'moment';
import t from 'react-translate';
import { Submission } from 'redux/schemas/models/submissions';

import NvIcon from 'shared/components/nv-icon';
import { calculateTimeDifference, useApprovalStatus } from 'shared/components/submission/utils';

const REVISION_DATE_STRINGS = {
  days: 'REVISE_IN_DAYS',
  hours: 'REVISE_IN_HOURS',
  minutes: 'REVISE_IN_MINUTES',
  date: 'DUE_DEADLINE',
};

const DUE_DATE_STRINGS = {
  days: 'DUE_IN_DAYS',
  hours: 'DUE_IN_HOURS',
  minutes: 'DUE_IN_MINUTES',
  date: 'DUE_DEADLINE',
};

type SubmissionExtraContentProps = {
  submission: Submission
  showDueDate?: boolean
  hideSocial?: boolean
  showRevisionRemainderTime?: boolean
};

const SubmissionExtraContent: React.FC<SubmissionExtraContentProps> = ({
  submission,
  showDueDate,
  hideSocial,
  showRevisionRemainderTime,
}) => {
  const {
    exercise,
    isVotable,
    votesCount,
    numPostsAndComments,
    submitting: { submissionStatus },
  } = submission;
  const { isApproved, isCompleted, isRejectedCanRevise } = useApprovalStatus(submissionStatus, exercise?.hardDeadline, exercise?.deadline);

  const getDateText = useCallback(() => {
    if (!exercise) return { isDueSoon: false, dateText: '' };

    const effectiveDeadline = exercise.extendedDeadline || exercise.deadline;
    if (!effectiveDeadline) return { isDueSoon: false, dateText: '' };

    let translateKeys = null;
    if (showRevisionRemainderTime && isRejectedCanRevise) {
      translateKeys = REVISION_DATE_STRINGS;
    } else if (showDueDate && !(isCompleted || isApproved)) {
      translateKeys = DUE_DATE_STRINGS;
    }

    if (!translateKeys) return { isDueSoon: false, dateText: '' };

    const dueDateMoment = moment(effectiveDeadline);
    const { days, hours, minutes } = calculateTimeDifference(dueDateMoment);

    if (days > 0) {
      return {
        isDueSoon: days <= 7,
        dateText: days <= 7
          ? t.TIMELINE.EXERCISE_DUE[translateKeys.days](days)
          : t.TIMELINE[translateKeys.date](dueDateMoment.format('MOMENT.MONTH_DAY_YEAR')),
      };
    } if (hours > 0) {
      return { isDueSoon: true, dateText: t.TIMELINE.EXERCISE_DUE[translateKeys.hours](hours) };
    }
    return { isDueSoon: true, dateText: t.TIMELINE.EXERCISE_DUE[translateKeys.minutes](minutes || 1) };
  }, [exercise, isApproved, isCompleted, isRejectedCanRevise, showDueDate, showRevisionRemainderTime]);

  return (
    <div className='extra flex flex-column align-items-center'>
      {showDueDate && (
        <div
          className={`text-small ${getDateText().isDueSoon ? 'text-danger' : 'text-gray-2'}`}
          aria-live='polite'
        >
          {getDateText().dateText}
        </div>
      )}
      {!hideSocial && (
        <div className='social'>
          {isVotable && votesCount > 0 && (
            <div className={`likes ${submission.liked ? 'text-primary' : 'text-gray-2'}`}>
              <NvIcon
                icon='like'
                size='smallest'
                role='img'
                altLabel={t.PRACTICE_ROOM.STATS.LIKE(votesCount)}
              />
              <span
                className='text-small'
                aria-hidden='true'
              >
                {votesCount}
              </span>
            </div>
          )}
          {numPostsAndComments > 0 && (
            <div className={`comments ${submission.commented || submission.posted ? 'text-primary' : 'text-gray-2'}`}>
              <NvIcon
                icon='comments'
                size='smallest'
                role='img'
                altLabel={t.PRACTICE_ROOM.STATS.COMMENT(numPostsAndComments)}
              />
              <span
                className='text-small'
                aria-hidden='true'
              >
                {numPostsAndComments}
              </span>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default SubmissionExtraContent;
